import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import DocumentViewer from '../components/DocumentViewer';
import DocumentCompare from '../components/DocumentCompare';
import ExportMenu from '../components/ExportMenu';

const DocumentLibraryPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const latestViewRequest = useRef(0);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/projects/${projectId}/documents/library`);
      setDocuments(data || []);
    } catch (error) {
      toast.error('Failed to load document library');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const openDocument = async (docId) => {
    const requestId = Date.now();
    latestViewRequest.current = requestId;
    try {
      const { data } = await api.get(`/api/documents/${docId}/view`);
      if (latestViewRequest.current !== requestId) {
        return;
      }
      setSelectedDoc(data);
      setDocuments((prev) => prev.map((doc) => (doc.id === data.id ? data : doc)));
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  return (
    <div className="panel">
      <div className="comparison-header">
        <div>
          <h1>Document Library</h1>
          <p className="muted">View uploaded documents without creating a merge.</p>
        </div>
        <button className="btn" onClick={() => navigate(`/projects/${projectId}`)}>
          Back to Project
        </button>
      </div>

      <div className="document-library">
        <aside className="document-library__list">
          {loading && <div className="loading">Loading documents...</div>}
          {!loading && documents.length === 0 && <div className="empty-state">No documents uploaded yet.</div>}
          {!loading && documents.map((doc) => (
            <button
              key={doc.id}
              className={`document-item ${selectedDoc?.id === doc.id ? 'active' : ''}`}
              onClick={() => openDocument(doc.id)}
            >
              <strong>{doc.document_type.replace('_', ' ').toUpperCase()}</strong>
              <span>{doc.filename || `Document ${doc.id}`}</span>
              <small>Views: {doc.view_count || 0}</small>
            </button>
          ))}
        </aside>

        <section className="document-library__viewer">
          <DocumentViewer document={selectedDoc} />
          {selectedDoc && (
            <div className="stack-gap-sm">
              <ExportMenu documentId={selectedDoc.id} />
            </div>
          )}
        </section>
      </div>

      <DocumentCompare documents={documents} />
    </div>
  );
};

export default DocumentLibraryPage;
